import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, MapPin, Layers, Calculator, Settings, RefreshCcw, Loader2, Info, History, FileSearch, RotateCcw, TrendingUp, Download, Upload, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * Admin — Pricing Engine V2 (iter65).
 * Isolated dashboard for the new dynamic pricing engine. Legacy fare-table
 * UI at /admin/prices continues to exist untouched.
 *
 * Requires the backend feature flag PRICING_ENGINE_V2_ENABLED=true. When
 * the flag is off the page still lets an admin view/edit config, but
 * order snapshots won't be produced (banner shown at top).
 */

const FORMULAS = [
  { value: "flat",             label: "Flat price" },
  { value: "base_km",          label: "Base + KM" },
  { value: "base_km_time",     label: "Base + KM + Time" },
  { value: "base_service_fee", label: "Base + Service fee" },
  { value: "weight_size",      label: "Weight / Size" },
  { value: "hourly",           label: "Hourly" },
];

const CATEGORIES = [
  "taxi", "towing", "crane", "bus", "moving", "delivery",
  "mechanic", "tire", "carwash", "luxury", "transport",
];

const TABS = [
  { id: "rules",      label: "Pricing Rules",     icon: Layers },
  { id: "zones",      label: "Zones",             icon: MapPin },
  { id: "options",    label: "Options",           icon: Settings },
  { id: "preview",    label: "Preview",           icon: Calculator },
  { id: "snapshot",   label: "Order Snapshot",    icon: FileSearch },
  { id: "drift",      label: "Drift Report",      icon: TrendingUp },
  { id: "promotions", label: "Recently Promoted", icon: ShieldCheck },
];

export default function AdminPricingV2() {
  const [tab, setTab] = useState("rules");
  const [flag, setFlag] = useState(null);

  useEffect(() => {
    api.get("/admin/pricing-v2/flag").then((r) => setFlag(r.data?.enabled)).catch(() => setFlag(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="admin-pricing-v2">
      <header className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">Pricing Engine V2</h1>
            <p className="text-white/50 mt-1 text-sm">
              Dynamic pricing: <span className="text-white/70">Country → Zone → Category → Option → Rule</span>.
              Legacy fare-table remains untouched at <a className="underline text-white/70" href="/admin/prices">/admin/prices</a>.
            </p>
          </div>
          <FlagBadge enabled={flag} />
        </div>
      </header>

      <nav className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto" data-testid="pv2-tabs">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`px-4 py-2.5 text-sm font-bold rounded-t-lg flex items-center gap-2 whitespace-nowrap transition-colors ${
              tab === tb.id ? "bg-white/10 text-white border-b-2 border-white" : "text-white/60 hover:text-white"
            }`}
            data-testid={`pv2-tab-${tb.id}`}
          >
            <tb.icon size={15} /> {tb.label}
          </button>
        ))}
      </nav>

      {tab === "rules"      && <RulesTab />}
      {tab === "zones"      && <ZonesTab />}
      {tab === "options"    && <OptionsTab />}
      {tab === "preview"    && <PreviewTab />}
      {tab === "snapshot"   && <SnapshotExplorerTab />}
      {tab === "drift"      && <DriftReportTab />}
      {tab === "promotions" && <PromotionsTab />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// PROMOTIONS AUDIT — immutable log of rules moved from [TEST] → Production
// ────────────────────────────────────────────────────────────────────────
function PromotionsTab() {
  const [rows, setRows] = useState([]);
  const [weeklyStatus, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    Promise.all([
      api.get("/admin/pricing-v2/promotions"),
      api.get("/admin/pricing-v2/weekly-drift-report/status"),
    ]).then(([p, w]) => { setRows(p.data || []); setWeekly(w.data); })
      .finally(() => setLoading(false));
  }, []);

  const sendTestNow = async () => {
    if (!window.confirm("Send a test drift email now to the configured admin address?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/pricing-v2/weekly-drift-report/send-now");
      if (data.sent) {
        toast.success(`Email sent · id ${data.resend_response?.id?.slice(0,8)}`);
      } else {
        toast.error(`Failed: ${data.resend_response?.message || data.reason || "unknown"}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const fmtVals = (v) => `base=${v?.base_fare ?? "—"} · per_km=${v?.per_km ?? "—"} · per_min=${v?.per_minute ?? "—"} · min=${v?.min_fare ?? "—"}`;

  return (
    <div data-testid="pv2-promotions" className="space-y-4">
      {weeklyStatus && (
        <div className={`rounded-xl border p-4 ${
          weeklyStatus.enabled ? "border-emerald-500/30 bg-emerald-500/5" : "border-orange-500/30 bg-orange-500/5"
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-black">Weekly Drift Report scheduler</div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                weeklyStatus.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-orange-500/20 text-orange-300"
              }`}>{weeklyStatus.enabled ? "Enabled" : "Disabled"}</span>
              {weeklyStatus.enabled && (
                <button onClick={sendTestNow} disabled={sending}
                        className="rounded-lg px-3 py-1 text-[11px] font-bold bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 inline-flex items-center gap-1.5"
                        data-testid="pv2-drift-send-now">
                  {sending && <Loader2 size={11} className="animate-spin" />} Send test now
                </button>
              )}
            </div>
          </div>
          <div className="text-xs text-white/60 mt-1">{weeklyStatus.note}</div>
          {weeklyStatus.enabled && (
            <div className="text-[11px] text-white/50 mt-1">
              Sender: <span className="font-mono">{weeklyStatus.sender_email}</span> · Recipient: <span className="font-mono">{weeklyStatus.admin_recipient}</span>
            </div>
          )}
          {weeklyStatus.missing_deps?.length > 0 && (
            <div className="text-[11px] text-orange-300 mt-2">
              Missing: <span className="font-mono">{weeklyStatus.missing_deps.join(", ")}</span>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-ink-border overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-border flex items-center justify-between">
          <div className="text-sm font-black">Recently Promoted Rules</div>
          <div className="text-[11px] text-white/50">{rows.length} promotion event(s) · immutable audit log</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-ink-surface2/60 text-white/60 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="px-3 py-2 text-start">Scope</th>
                <th className="px-3 py-2 text-start">Category / Option</th>
                <th className="px-3 py-2 text-start">Previous → New</th>
                <th className="px-3 py-2 text-start">Current values</th>
                <th className="px-3 py-2 text-start">Approved</th>
                <th className="px-3 py-2 text-start">Rule</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-3 py-6 text-center text-white/50"><Loader2 size={16} className="animate-spin inline" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-white/50">No promotions yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.rule_id} className="border-t border-ink-border hover:bg-white/[0.02]">
                  <td className="px-3 py-2 font-bold">{r.country}{r.zone_name ? ` / ${r.zone_name}` : ""}</td>
                  <td className="px-3 py-2">{r.category} / {r.option_code || "any"}</td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-500/15 text-orange-300">{r.previous_status}</span>
                    <span className="mx-1 text-white/40">→</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300">{r.new_status}</span>
                  </td>
                  <td className="px-3 py-2 text-white/70 font-mono text-[10px]">{fmtVals(r.current_values)} {r.currency}</td>
                  <td className="px-3 py-2 text-white/60 text-[11px]">
                    <div>{r.approved_by || "admin"}</div>
                    <div className="text-white/40">{r.approved_at?.slice(0,19)?.replace("T"," ")}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/50">{r.rule_id?.slice(0,14)} · v{r.current_rule_version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-[10px] text-white/40">
        Each promotion event is derived from the immutable <code>pricing_rule_versions</code> archive — the row that overwrote a [STARTER/TEST] rule with a Production one.
      </div>
    </div>
  );
}

function FlagBadge({ enabled }) {
  if (enabled === null) return null;
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-widest border ${
        enabled ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-orange-500/10 text-orange-300 border-orange-500/30"
      }`}
      data-testid="pv2-flag-badge"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${enabled ? "bg-green-400" : "bg-orange-400"}`} />
      V2 {enabled ? "Enabled" : "Off (view-only)"}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// RULES
// ────────────────────────────────────────────────────────────────────────

function RulesTab() {
  const [rules, setRules] = useState([]);
  const [zones, setZones] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCountry, setFilterCountry] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [editing, setEditing] = useState(null); // rule doc or {} for new

  const load = async () => {
    setLoading(true);
    try {
      const [r, z, o] = await Promise.all([
        api.get("/admin/pricing-v2/rules"),
        api.get("/admin/pricing-v2/zones"),
        api.get("/admin/pricing-v2/options"),
      ]);
      setRules(r.data || []);
      setZones(z.data || []);
      setOptions(o.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rules.filter((r) => {
    if (filterCountry && (r.country || "").toUpperCase() !== filterCountry.toUpperCase()) return false;
    if (filterCategory && (r.category || "").toLowerCase() !== filterCategory.toLowerCase()) return false;
    return true;
  }), [rules, filterCountry, filterCategory]);

  const seedSamples = async () => {
    try {
      const { data } = await api.post("/admin/pricing-v2/seed-samples");
      toast.success(`Seeded — rules total: ${data.rules_total}, new: ${data.rules_inserted_now}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (rid) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/admin/pricing-v2/rules/${rid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [historyRule, setHistoryRule] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const countries = Array.from(new Set(rules.map((r) => r.country))).sort();
  const zoneNameById = Object.fromEntries(zones.map((z) => [z.zone_id, z.name]));

  return (
    <div data-testid="pv2-rules">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mb-1">Country</div>
          <select className="bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm"
                  value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}
                  data-testid="pv2-rules-filter-country">
            <option value="">All countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mb-1">Category</div>
          <select className="bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm"
                  value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                  data-testid="pv2-rules-filter-category">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="ms-auto flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-ink-border hover:bg-white/5" data-testid="pv2-rules-refresh">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />}
          </button>
          <button onClick={seedSamples}
                  className="rounded-lg px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/15 border border-white/10"
                  data-testid="pv2-rules-seed">
            Seed samples
          </button>
          <button onClick={() => setBulkOpen(true)}
                  className="rounded-lg px-3 py-2 text-xs font-bold bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 inline-flex items-center gap-1.5"
                  data-testid="pv2-rules-bulk-upload">
            <Upload size={13} /> Bulk CSV
          </button>
          <button onClick={() => setEditing({})}
                  className="rounded-lg px-3 py-2 text-sm font-bold bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
                  data-testid="pv2-rules-new">
            <Plus size={14} /> New rule
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-ink-border">
        <table className="w-full text-sm">
          <thead className="bg-ink-surface2/60 text-white/60 text-[11px] uppercase tracking-widest">
            <tr>
              <th className="px-3 py-2 text-start">Country</th>
              <th className="px-3 py-2 text-start">Zone</th>
              <th className="px-3 py-2 text-start">Category</th>
              <th className="px-3 py-2 text-start">Option</th>
              <th className="px-3 py-2 text-start">Formula</th>
              <th className="px-3 py-2 text-end">Base</th>
              <th className="px-3 py-2 text-end">KM</th>
              <th className="px-3 py-2 text-end">Min</th>
              <th className="px-3 py-2 text-end">Comm %</th>
              <th className="px-3 py-2 text-start">Cur</th>
              <th className="px-3 py-2 text-center">Active</th>
              <th className="px-3 py-2 text-center">v</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={13} className="px-3 py-8 text-center text-white/50">No rules. Click "Seed samples" to add starter rows.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.rule_id} className="border-t border-ink-border hover:bg-white/[0.02]">
                <td className="px-3 py-2 font-bold">{r.country}</td>
                <td className="px-3 py-2 text-white/70">{r.zone_id ? (zoneNameById[r.zone_id] || r.zone_id.slice(0, 8)) : "—"}</td>
                <td className="px-3 py-2">{r.category}</td>
                <td className="px-3 py-2 text-white/70">{r.option_code || "any"}</td>
                <td className="px-3 py-2 text-white/60 text-xs font-mono">{r.formula}</td>
                <td className="px-3 py-2 text-end tabular-nums">{r.base_fare ?? "—"}</td>
                <td className="px-3 py-2 text-end tabular-nums">{r.per_km ?? "—"}</td>
                <td className="px-3 py-2 text-end tabular-nums">{r.min_fare ?? "—"}</td>
                <td className="px-3 py-2 text-end tabular-nums">{r.commission_percent}%</td>
                <td className="px-3 py-2 text-white/70">{r.currency}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block w-2 h-2 rounded-full ${r.active ? "bg-green-500" : "bg-gray-500"}`} />
                </td>
                <td className="px-3 py-2 text-center text-white/50">{r.version}</td>
                <td className="px-3 py-2 text-end">
                  <button onClick={() => setHistoryRule(r)} className="p-1.5 hover:bg-white/5 rounded" title="Version history" data-testid={`pv2-rule-history-${r.rule_id}`}>
                    <History size={14} />
                  </button>
                  <button onClick={() => setEditing(r)} className="p-1.5 hover:bg-white/5 rounded" data-testid={`pv2-rule-edit-${r.rule_id}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => del(r.rule_id)} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded" data-testid={`pv2-rule-del-${r.rule_id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <RuleEditor rule={editing} zones={zones} options={options} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {historyRule && <RuleHistoryModal rule={historyRule} onClose={() => setHistoryRule(null)} onRestored={() => { setHistoryRule(null); load(); }} />}
      {bulkOpen && <BulkUploadModal onClose={() => setBulkOpen(false)} onApplied={() => { setBulkOpen(false); load(); }} />}
    </div>
  );
}

/** Bulk CSV upload with a mandatory dry-run diff preview.
 *
 * The Apply button stays disabled until the admin has previewed the diff
 * for the CURRENT csv text. Any edit to the textarea invalidates the
 * preview — you have to preview again before applying. This is the
 * safety rail preventing "hit Enter and change 40 rules by accident". */
function BulkUploadModal({ onClose, onApplied }) {
  const [csv, setCsv] = useState(
    "country,zone_id,category,option_code,base_fare,per_km,per_minute,min_fare,booking_fee,commission_percent\n"
  );
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [includeNew, setIncludeNew] = useState(false);
  const [csvFingerprint, setCsvFingerprint] = useState("");

  // Any keystroke invalidates the last preview so admins can't apply stale data.
  const onCsvChange = (v) => { setCsv(v); setPreview(null); setCsvFingerprint(""); };

  const readFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    onCsvChange(text);
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pricing-v2/rules/bulk-upload", { csv, dry_run: true });
      setPreview(data);
      setCsvFingerprint(csv);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const applyChanges = async () => {
    if (!preview) { toast.error("Preview first"); return; }
    if (csv !== csvFingerprint) { toast.error("CSV changed since preview — preview again"); return; }
    const upd = preview.summary.updates_pending;
    const news = includeNew ? preview.summary.new_rules : 0;
    const g = preview.drift_guard;
    if (g && g.blocks_apply) {
      const ok = window.confirm(
        `⚠️ HIGH-IMPACT PRICING CHANGE\n\n` +
        `This change would drift recent orders by ${g.average_drift_pct}% on average ` +
        `(max ${g.max_drift_pct}%) across ${g.orders_impacted} order(s).\n` +
        `Cumulative price impact: ${g.cumulative_price_impact}.\n\n` +
        `Threshold is ${g.threshold_pct}%. Existing orders keep their booking-time ` +
        `snapshots and are NOT modified — but future orders will use the new prices.\n\n` +
        `Type OK to proceed. Cancel to reconsider.`
      );
      if (!ok) return;
    } else {
      if (!window.confirm(`Apply ${upd} update(s)${news ? ` + ${news} new rule(s)` : ""}?\n\nExisting orders keep their booking-time snapshots and are NOT affected.`)) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pricing-v2/rules/bulk-upload", {
        csv, dry_run: false, include_new: includeNew,
        force: !!(g && g.blocks_apply),
      });
      if (data.blocked_by_guard) {
        toast.error("Blocked by drift guard — refresh and retry");
        return;
      }
      toast.success(`Applied ${data.applied} change(s)`);
      onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const canApply = preview && csv === csvFingerprint && (preview.summary.updates_pending > 0 || (includeNew && preview.summary.new_rules > 0)) && preview.errors.length === 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-bg border border-ink-border rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pv2-bulk-modal">
        <div className="border-b border-ink-border px-6 py-4 flex justify-between items-center">
          <div>
            <div className="text-lg font-black">Bulk Upload Pricing Rules (CSV)</div>
            <div className="text-[11px] text-white/50">Dry-run first · changes stay draft until you click Apply · orders untouched</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div className="rounded-lg border border-ink-border bg-ink-surface2/40 p-3 text-[11px] text-white/60">
            <div className="font-bold text-white/80 mb-1">CSV schema (header required)</div>
            <div className="font-mono text-[10px]">country,zone_id,category,option_code,base_fare,per_km,per_minute,min_fare,booking_fee,commission_percent</div>
            <div className="mt-1">Empty <code>zone_id</code>/<code>option_code</code> = country-default / any. Rules matched by (country + zone + category + option).</div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs text-white/60 me-auto">Paste CSV or upload a .csv file:</label>
              <input type="file" accept=".csv,text/csv" onChange={(e) => readFile(e.target.files?.[0])} className="text-xs" data-testid="pv2-bulk-file" />
            </div>
            <textarea value={csv} onChange={(e) => onCsvChange(e.target.value)}
                      rows={8}
                      className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-[11px] font-mono"
                      data-testid="pv2-bulk-csv" />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={runPreview} disabled={busy || !csv.trim()}
                    className="rounded-lg px-4 py-2 text-sm font-bold bg-white/10 hover:bg-white/15 border border-white/10 inline-flex items-center gap-2"
                    data-testid="pv2-bulk-preview">
              {busy && <Loader2 size={14} className="animate-spin" />} 1. Preview changes
            </button>
            <label className="inline-flex items-center gap-2 text-xs text-white/70">
              <input type="checkbox" checked={includeNew} onChange={(e) => setIncludeNew(e.target.checked)} data-testid="pv2-bulk-includenew" />
              Include new rules (rows that don't match any existing rule)
            </label>
          </div>

          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                <MetricBox label="Rows"     value={preview.summary.total_rows} />
                <MetricBox label="Updates"  value={preview.summary.updates_pending} accent={preview.summary.updates_pending ? "green" : ""} />
                <MetricBox label="No change" value={preview.summary.no_changes} />
                <MetricBox label="New rules" value={preview.summary.new_rules} sub={includeNew ? "will be inserted" : "will be skipped"} />
              </div>
              {preview.drift_guard && preview.drift_guard.orders_impacted > 0 && (
                <div className={`rounded-lg border p-3 text-xs ${
                  preview.drift_guard.blocks_apply
                    ? "border-red-500/40 bg-red-500/10 text-red-200"
                    : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
                }`} data-testid="pv2-bulk-drift-guard">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-sm">
                      {preview.drift_guard.blocks_apply
                        ? `⚠️ HIGH IMPACT — Drift ${preview.drift_guard.average_drift_pct}% > ${preview.drift_guard.threshold_pct}% threshold`
                        : `Drift impact preview`}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest opacity-70">
                      {preview.drift_guard.blocks_apply ? "requires force" : "within guard"}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
                    <div><span className="opacity-60">Impacted orders:</span> <span className="font-bold">{preview.drift_guard.orders_impacted}</span></div>
                    <div><span className="opacity-60">Avg drift:</span> <span className="font-bold">{preview.drift_guard.average_drift_pct}%</span></div>
                    <div><span className="opacity-60">Max drift:</span> <span className="font-bold">{preview.drift_guard.max_drift_pct}%</span></div>
                    <div><span className="opacity-60">Total impact:</span> <span className="font-bold tabular-nums">{preview.drift_guard.cumulative_price_impact > 0 ? "+" : ""}{preview.drift_guard.cumulative_price_impact}</span></div>
                  </div>
                  <div className="mt-2 text-[10px] opacity-70">
                    Simulated against the last 100 orders with V2 snapshots. Existing orders are NOT modified — only future orders use the new prices.
                  </div>
                </div>
              )}
              {preview.errors.length > 0 && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 p-3 text-xs">
                  <div className="font-bold mb-1">Errors ({preview.errors.length})</div>
                  {preview.errors.slice(0, 5).map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                </div>
              )}
              <div className="overflow-x-auto rounded-lg border border-ink-border max-h-[280px]">
                <table className="w-full text-[11px]">
                  <thead className="bg-ink-surface2/60 text-white/60 text-[10px] uppercase tracking-widest sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-start">Row</th>
                      <th className="px-2 py-1.5 text-start">Action</th>
                      <th className="px-2 py-1.5 text-start">Scope</th>
                      <th className="px-2 py-1.5 text-start">Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.diffs.map((d) => (
                      <tr key={d.row} className="border-t border-ink-border">
                        <td className="px-2 py-1.5 text-white/50">{d.row}</td>
                        <td className="px-2 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            d.action === "update" ? "bg-emerald-500/20 text-emerald-300" :
                            d.action === "create_new" ? "bg-blue-500/20 text-blue-300" :
                            "bg-white/10 text-white/50"
                          }`}>{d.action}</span>
                        </td>
                        <td className="px-2 py-1.5 text-white/80">{d.country}/{d.zone_id ? d.zone_id.slice(0,8) : "—"}/{d.category}/{d.option_code || "any"}</td>
                        <td className="px-2 py-1.5 text-white/70">
                          {d.action === "update" && Object.entries(d.changes || {}).map(([k, v]) => (
                            <div key={k} className="font-mono text-[10px]"><span className="text-white/50">{k}:</span> {v.from}→<span className="text-emerald-300">{v.to}</span></div>
                          ))}
                          {d.action === "no_change" && <span className="text-white/40">—</span>}
                          {d.action === "create_new" && <span className="text-blue-300">{JSON.stringify(d.proposed).slice(0,60)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-ink-border px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ink-border text-sm">Cancel</button>
          <button
            onClick={applyChanges}
            disabled={!canApply || busy}
            className="px-4 py-2 rounded-lg bg-emerald-500 text-black font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="pv2-bulk-apply"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            2. Apply changes
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleHistoryModal({ rule, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/pricing-v2/rules/${rule.rule_id}/versions`);
      setVersions(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [rule.rule_id]);  // eslint-disable-line

  const restore = async (v) => {
    if (!window.confirm(
      `Restore this rule to version ${v}?\n\n` +
      `A new version (${(rule.version || 1) + 1}) will be created with the restored values. ` +
      `Existing orders keep their original snapshot and are NOT affected.`
    )) return;
    setRestoring(v);
    try {
      await api.post(`/admin/pricing-v2/rules/${rule.rule_id}/restore/${v}`);
      toast.success(`Restored to v${v}`);
      onRestored();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-bg border border-ink-border rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pv2-history-modal">
        <div className="border-b border-ink-border px-6 py-4 flex justify-between items-center">
          <div>
            <div className="text-lg font-black">Version History</div>
            <div className="text-xs text-white/50 mt-0.5">
              {rule.country} · {rule.category} · {rule.option_code || "any"} · current v{rule.version}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading && <div className="text-white/50 text-sm py-6 text-center"><Loader2 size={20} className="animate-spin inline-block" /></div>}
          {!loading && versions.length === 0 && (
            <div className="text-white/50 text-sm py-6 text-center">
              No prior versions yet. Edit this rule to create the first archived version.
            </div>
          )}
          {!loading && versions.length > 0 && (
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.version_id} className="rounded-xl border border-ink-border bg-ink-surface2/30 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-black tabular-nums">v{v.version}</span>
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/60">{v.action || "update"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-white/50">{v.archived_at?.slice(0, 19).replace("T", " ")}</span>
                      <button
                        onClick={() => restore(v.version)}
                        disabled={restoring === v.version}
                        className="rounded-lg px-3 py-1.5 text-xs font-bold bg-white/10 hover:bg-white/15 border border-white/10 inline-flex items-center gap-1.5"
                        data-testid={`pv2-restore-${v.version}`}
                      >
                        {restoring === v.version && <Loader2 size={12} className="animate-spin" />}
                        <RotateCcw size={12} /> Restore
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs text-white/70">
                    <RowKV k="Formula" v={v.formula} />
                    <RowKV k="Base" v={v.base_fare} />
                    <RowKV k="Per km" v={v.per_km} />
                    <RowKV k="Per min" v={v.per_minute} />
                    <RowKV k="Min fare" v={v.min_fare} />
                    <RowKV k="Booking" v={v.booking_fee} />
                    <RowKV k="Commission" v={v.commission_percent + "%"} />
                    <RowKV k="Currency" v={v.currency} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RowKV({ k, v }) {
  if (v === null || v === undefined || v === "") return <div><span className="text-white/40">{k}</span> <span className="text-white/60">—</span></div>;
  return <div><span className="text-white/40">{k}</span> <span className="tabular-nums font-mono text-white/85">{String(v)}</span></div>;
}

function RuleEditor({ rule, zones, options, onClose, onSaved }) {
  const isNew = !rule.rule_id;
  const [form, setForm] = useState({
    country: rule.country || "IL",
    zone_id: rule.zone_id || "",
    category: rule.category || "taxi",
    option_code: rule.option_code || "",
    currency: rule.currency || "ILS",
    formula: rule.formula || "base_km_time",
    base_fare: rule.base_fare ?? 12,
    per_km: rule.per_km ?? 6.5,
    per_minute: rule.per_minute ?? 0.5,
    min_fare: rule.min_fare ?? 25,
    flat_price: rule.flat_price ?? null,
    booking_fee: rule.booking_fee ?? null,
    service_fee: rule.service_fee ?? null,
    waiting_fee_per_min: rule.waiting_fee_per_min ?? null,
    extra_stop_fee: rule.extra_stop_fee ?? null,
    surcharge_percent: rule.surcharge_percent ?? 0,
    weight_price_per_kg: rule.weight_price_per_kg ?? null,
    size_price_per_m3: rule.size_price_per_m3 ?? null,
    hourly_rate: rule.hourly_rate ?? null,
    commission_percent: rule.commission_percent ?? 15,
    active: rule.active ?? true,
    notes: rule.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const catOptions = options.filter((o) => o.category === form.category);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Empty strings → null
      ["zone_id", "option_code"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      // Numeric coercion + null preservation
      ["base_fare","per_km","per_minute","min_fare","flat_price","booking_fee",
       "service_fee","waiting_fee_per_min","extra_stop_fee","surcharge_percent",
       "weight_price_per_kg","size_price_per_m3","hourly_rate","commission_percent"
      ].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      if (payload.surcharge_percent === null) payload.surcharge_percent = 0;
      if (payload.commission_percent === null) payload.commission_percent = 15;
      if (isNew) await api.post("/admin/pricing-v2/rules", payload);
      else await api.patch(`/admin/pricing-v2/rules/${rule.rule_id}`, payload);
      toast.success(isNew ? "Rule created" : "Rule updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // Which rate fields matter for the current formula.
  const need_ = (k) => {
    const f = form.formula;
    const map = {
      base_fare:     ["base_km","base_km_time","base_service_fee","weight_size"],
      per_km:        ["base_km","base_km_time"],
      per_minute:    ["base_km_time"],
      flat_price:    ["flat"],
      service_fee:   ["base_service_fee"],
      hourly_rate:   ["hourly"],
      weight_price_per_kg: ["weight_size"],
      size_price_per_m3:   ["weight_size"],
    };
    return (map[k] || []).includes(f);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-bg border border-ink-border rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pv2-rule-editor">
        <div className="sticky top-0 bg-ink-bg/95 backdrop-blur border-b border-ink-border px-6 py-4 flex items-center justify-between">
          <div className="text-lg font-black">{isNew ? "New Pricing Rule" : "Edit Pricing Rule"}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 grid md:grid-cols-2 gap-4">
          <Field label="Country (ISO, or GLOBAL)">
            <input type="text" value={form.country} onChange={(e) => set("country", e.target.value.toUpperCase())}
                   className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-country" />
          </Field>
          <Field label="Currency (ISO)">
            <input type="text" value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())}
                   className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-currency" />
          </Field>
          <Field label="Zone (optional, overrides country)">
            <select value={form.zone_id} onChange={(e) => set("zone_id", e.target.value)}
                    className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-zone">
              <option value="">— country default —</option>
              {zones.filter((z) => (z.country || "").toUpperCase() === form.country.toUpperCase()).map((z) =>
                <option key={z.zone_id} value={z.zone_id}>{z.name} ({z.radius_km}km)</option>
              )}
            </select>
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)}
                    className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Option (optional)">
            <select value={form.option_code || ""} onChange={(e) => set("option_code", e.target.value)}
                    className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-option">
              <option value="">— any option (category default) —</option>
              {catOptions.map((o) => <option key={o.option_id} value={o.code}>{o.labels?.en || o.code}</option>)}
            </select>
          </Field>
          <Field label="Formula">
            <select value={form.formula} onChange={(e) => set("formula", e.target.value)}
                    className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-formula">
              {FORMULAS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>

          {need_("base_fare")           && <NumField label="Base fare"           v={form.base_fare}           onChange={(v) => set("base_fare", v)} testid="pv2-edit-base" />}
          {need_("per_km")              && <NumField label="Per km"              v={form.per_km}              onChange={(v) => set("per_km", v)} testid="pv2-edit-perkm" />}
          {need_("per_minute")          && <NumField label="Per minute"          v={form.per_minute}          onChange={(v) => set("per_minute", v)} testid="pv2-edit-permin" />}
          {need_("flat_price")          && <NumField label="Flat price"          v={form.flat_price}          onChange={(v) => set("flat_price", v)} testid="pv2-edit-flat" />}
          {need_("service_fee")         && <NumField label="Service fee"         v={form.service_fee}         onChange={(v) => set("service_fee", v)} testid="pv2-edit-service" />}
          {need_("hourly_rate")         && <NumField label="Hourly rate"         v={form.hourly_rate}         onChange={(v) => set("hourly_rate", v)} testid="pv2-edit-hourly" />}
          {need_("weight_price_per_kg") && <NumField label="Price per kg"        v={form.weight_price_per_kg} onChange={(v) => set("weight_price_per_kg", v)} testid="pv2-edit-kg" />}
          {need_("size_price_per_m3")   && <NumField label="Price per m³"        v={form.size_price_per_m3}   onChange={(v) => set("size_price_per_m3", v)} testid="pv2-edit-m3" />}

          <NumField label="Minimum fare"     v={form.min_fare}           onChange={(v) => set("min_fare", v)} testid="pv2-edit-min" />
          <NumField label="Booking fee"      v={form.booking_fee}        onChange={(v) => set("booking_fee", v)} testid="pv2-edit-booking" />
          <NumField label="Waiting / min"    v={form.waiting_fee_per_min} onChange={(v) => set("waiting_fee_per_min", v)} testid="pv2-edit-waiting" />
          <NumField label="Extra stop fee"   v={form.extra_stop_fee}     onChange={(v) => set("extra_stop_fee", v)} testid="pv2-edit-stops" />
          <NumField label="Surcharge %"      v={form.surcharge_percent}  onChange={(v) => set("surcharge_percent", v)} testid="pv2-edit-surcharge" />
          <NumField label="Drivons commission %" v={form.commission_percent} onChange={(v) => set("commission_percent", v)} testid="pv2-edit-commission" />

          <Field label="Notes" full>
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-edit-notes" />
          </Field>

          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="pv2-edit-active" />
            Active
          </label>
        </div>
        <div className="sticky bottom-0 bg-ink-bg/95 backdrop-blur border-t border-ink-border px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ink-border hover:bg-white/5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-lg bg-white text-black hover:bg-white/90 text-sm font-bold inline-flex items-center gap-2"
                  data-testid="pv2-edit-save">
            {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mb-1">{label}</div>
      {children}
    </label>
  );
}

function NumField({ label, v, onChange, testid }) {
  return (
    <Field label={label}>
      <input type="number" step="0.01" min="0" value={v ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
             className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm tabular-nums" data-testid={testid} />
    </Field>
  );
}

// ────────────────────────────────────────────────────────────────────────
// ZONES
// ────────────────────────────────────────────────────────────────────────

function ZonesTab() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/pricing-v2/zones"); setZones(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    try { await api.delete(`/admin/pricing-v2/zones/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="pv2-zones">
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({})}
                className="rounded-lg px-3 py-2 text-sm font-bold bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
                data-testid="pv2-zone-new">
          <Plus size={14} /> New zone
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-ink-border">
        <table className="w-full text-sm">
          <thead className="bg-ink-surface2/60 text-white/60 text-[11px] uppercase tracking-widest">
            <tr>
              <th className="px-3 py-2 text-start">Country</th>
              <th className="px-3 py-2 text-start">Name</th>
              <th className="px-3 py-2 text-end">Center Lat</th>
              <th className="px-3 py-2 text-end">Center Lng</th>
              <th className="px-3 py-2 text-end">Radius km</th>
              <th className="px-3 py-2 text-center">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {zones.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-white/50">No zones yet.</td></tr>}
            {zones.map((z) => (
              <tr key={z.zone_id} className="border-t border-ink-border hover:bg-white/[0.02]">
                <td className="px-3 py-2 font-bold">{z.country}</td>
                <td className="px-3 py-2">{z.name}</td>
                <td className="px-3 py-2 text-end tabular-nums">{z.center_lat.toFixed(4)}</td>
                <td className="px-3 py-2 text-end tabular-nums">{z.center_lng.toFixed(4)}</td>
                <td className="px-3 py-2 text-end tabular-nums">{z.radius_km}</td>
                <td className="px-3 py-2 text-center"><span className={`inline-block w-2 h-2 rounded-full ${z.active ? "bg-green-500" : "bg-gray-500"}`} /></td>
                <td className="px-3 py-2 text-end">
                  <button onClick={() => setEditing(z)} className="p-1.5 hover:bg-white/5 rounded" data-testid={`pv2-zone-edit-${z.zone_id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(z.zone_id)} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded" data-testid={`pv2-zone-del-${z.zone_id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ZoneEditor zone={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ZoneEditor({ zone, onClose, onSaved }) {
  const isNew = !zone.zone_id;
  const [form, setForm] = useState({
    country: zone.country || "IL",
    name: zone.name || "",
    center_lat: zone.center_lat ?? 32.0853,
    center_lng: zone.center_lng ?? 34.7818,
    radius_km: zone.radius_km ?? 15,
    active: zone.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form,
        center_lat: Number(form.center_lat), center_lng: Number(form.center_lng),
        radius_km: Number(form.radius_km) };
      if (isNew) await api.post("/admin/pricing-v2/zones", payload);
      else await api.patch(`/admin/pricing-v2/zones/${zone.zone_id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-bg border border-ink-border rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="pv2-zone-editor">
        <div className="border-b border-ink-border px-6 py-4 flex justify-between items-center">
          <div className="text-lg font-black">{isNew ? "New Zone" : "Edit Zone"}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <Field label="Country"><input value={form.country} onChange={(e) => set("country", e.target.value.toUpperCase())} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-zone-country" /></Field>
          <Field label="Name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-zone-name" /></Field>
          <NumField label="Center latitude" v={form.center_lat} onChange={(v) => set("center_lat", v)} testid="pv2-zone-lat" />
          <NumField label="Center longitude" v={form.center_lng} onChange={(v) => set("center_lng", v)} testid="pv2-zone-lng" />
          <NumField label="Radius (km)" v={form.radius_km} onChange={(v) => set("radius_km", v)} testid="pv2-zone-radius" />
          <label className="flex items-center gap-2 mt-6 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="pv2-zone-active" /> Active
          </label>
        </div>
        <div className="border-t border-ink-border px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ink-border text-sm">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-lg bg-white text-black text-sm font-bold inline-flex items-center gap-2" data-testid="pv2-zone-save">
            {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// OPTIONS
// ────────────────────────────────────────────────────────────────────────

function OptionsTab() {
  const [opts, setOpts] = useState([]);
  const [filterCategory, setFilterCategory] = useState("");
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/pricing-v2/options"); setOpts(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filterCategory ? opts.filter((o) => o.category === filterCategory) : opts;

  const del = async (id) => {
    if (!window.confirm("Delete this option?")) return;
    try { await api.delete(`/admin/pricing-v2/options/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="pv2-options">
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mb-1">Category</div>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                  className="bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-opt-filter">
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button onClick={() => setEditing({})}
                className="rounded-lg px-3 py-2 text-sm font-bold bg-white text-black hover:bg-white/90 inline-flex items-center gap-2" data-testid="pv2-opt-new">
          <Plus size={14} /> New option
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-ink-border">
        <table className="w-full text-sm">
          <thead className="bg-ink-surface2/60 text-white/60 text-[11px] uppercase tracking-widest">
            <tr>
              <th className="px-3 py-2 text-start">Category</th>
              <th className="px-3 py-2 text-start">Code</th>
              <th className="px-3 py-2 text-start">EN</th>
              <th className="px-3 py-2 text-start">HE</th>
              <th className="px-3 py-2 text-center">Order</th>
              <th className="px-3 py-2 text-center">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-white/50">No options.</td></tr>}
            {filtered.map((o) => (
              <tr key={o.option_id} className="border-t border-ink-border hover:bg-white/[0.02]">
                <td className="px-3 py-2 font-bold">{o.category}</td>
                <td className="px-3 py-2 font-mono text-xs">{o.code}</td>
                <td className="px-3 py-2">{o.labels?.en}</td>
                <td className="px-3 py-2" dir="rtl">{o.labels?.he}</td>
                <td className="px-3 py-2 text-center text-white/60">{o.order}</td>
                <td className="px-3 py-2 text-center"><span className={`inline-block w-2 h-2 rounded-full ${o.active ? "bg-green-500" : "bg-gray-500"}`} /></td>
                <td className="px-3 py-2 text-end">
                  <button onClick={() => setEditing(o)} className="p-1.5 hover:bg-white/5 rounded"><Pencil size={14} /></button>
                  <button onClick={() => del(o.option_id)} className="p-1.5 hover:bg-red-500/10 text-red-400 rounded"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <OptionEditor opt={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function OptionEditor({ opt, onClose, onSaved }) {
  const isNew = !opt.option_id;
  const [form, setForm] = useState({
    category: opt.category || "taxi",
    code: opt.code || "",
    labels: opt.labels || { en: "", he: "", ar: "", ru: "", tr: "", fr: "" },
    order: opt.order ?? 0,
    active: opt.active ?? true,
    meta: opt.meta || {},
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLbl = (l, v) => setForm((f) => ({ ...f, labels: { ...f.labels, [l]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) };
      if (isNew) await api.post("/admin/pricing-v2/options", payload);
      else await api.patch(`/admin/pricing-v2/options/${opt.option_id}`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-bg border border-ink-border rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="pv2-opt-editor">
        <div className="border-b border-ink-border px-6 py-4 flex justify-between items-center">
          <div className="text-lg font-black">{isNew ? "New Option" : "Edit Option"}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <Field label="Category">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Code (unique within category)">
            <input value={form.code} onChange={(e) => set("code", e.target.value.toLowerCase())} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm font-mono" />
          </Field>
          {["en","he","ar","ru","tr","fr"].map((l) => (
            <Field key={l} label={`Label ${l.toUpperCase()}`}>
              <input value={form.labels[l] || ""} onChange={(e) => setLbl(l, e.target.value)}
                     dir={["he","ar"].includes(l) ? "rtl" : "ltr"}
                     className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" />
            </Field>
          ))}
          <NumField label="Order" v={form.order} onChange={(v) => set("order", v)} />
          <label className="flex items-center gap-2 mt-6 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active
          </label>
        </div>
        <div className="border-t border-ink-border px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-ink-border text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-white text-black text-sm font-bold inline-flex items-center gap-2" data-testid="pv2-opt-save">
            {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// PREVIEW CALCULATOR
// ────────────────────────────────────────────────────────────────────────

function PreviewTab() {
  const [zones, setZones] = useState([]);
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState({
    country: "IL", zone_id: "", category: "taxi", option_code: "regular",
    pickup_lat: 32.0853, pickup_lng: 34.7818,
    distance_km: 10, duration_min: 20,
    weight_kg: 0, volume_m3: 0, hours: 0,
    extra_stops: 0, waiting_min: 0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing-v2/zones").then((r) => setZones(r.data || []));
    api.get("/admin/pricing-v2/options").then((r) => setOptions(r.data || []));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const catOptions = options.filter((o) => o.category === form.category);
  const filteredZones = zones.filter((z) => (z.country || "").toUpperCase() === form.country.toUpperCase());

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const payload = { ...form,
        zone_id: form.zone_id || null,
        option_code: form.option_code || null,
        distance_km: Number(form.distance_km) || 0,
        duration_min: Number(form.duration_min) || 0,
        weight_kg: Number(form.weight_kg) || 0,
        volume_m3: Number(form.volume_m3) || 0,
        hours: Number(form.hours) || 0,
        extra_stops: Number(form.extra_stops) || 0,
        waiting_min: Number(form.waiting_min) || 0,
        pickup_lat: Number(form.pickup_lat) || null,
        pickup_lng: Number(form.pickup_lng) || null,
      };
      const { data } = await api.post("/admin/pricing-v2/preview", payload);
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="pv2-preview" className="grid md:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-ink-border p-5">
        <div className="text-sm font-black uppercase tracking-widest text-white/60 mb-4 flex items-center gap-2">
          <Calculator size={14} /> Inputs
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Country"><input value={form.country} onChange={(e) => set("country", e.target.value.toUpperCase())} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-prev-country" /></Field>
          <Field label="Zone (auto if empty)">
            <select value={form.zone_id} onChange={(e) => set("zone_id", e.target.value)} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-prev-zone">
              <option value="">— auto by lat/lng —</option>
              {filteredZones.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.name}</option>)}
            </select>
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => { set("category", e.target.value); set("option_code", ""); }} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-prev-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Option">
            <select value={form.option_code} onChange={(e) => set("option_code", e.target.value)} className="w-full bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm" data-testid="pv2-prev-option">
              <option value="">— any —</option>
              {catOptions.map((o) => <option key={o.option_id} value={o.code}>{o.labels?.en || o.code}</option>)}
            </select>
          </Field>
          <NumField label="Pickup latitude"  v={form.pickup_lat} onChange={(v) => set("pickup_lat", v)} testid="pv2-prev-lat" />
          <NumField label="Pickup longitude" v={form.pickup_lng} onChange={(v) => set("pickup_lng", v)} testid="pv2-prev-lng" />
          <NumField label="Distance (km)"    v={form.distance_km} onChange={(v) => set("distance_km", v)} testid="pv2-prev-km" />
          <NumField label="Duration (min)"   v={form.duration_min} onChange={(v) => set("duration_min", v)} testid="pv2-prev-min" />
          <NumField label="Weight (kg)"      v={form.weight_kg} onChange={(v) => set("weight_kg", v)} />
          <NumField label="Volume (m³)"      v={form.volume_m3} onChange={(v) => set("volume_m3", v)} />
          <NumField label="Hours"            v={form.hours} onChange={(v) => set("hours", v)} />
          <NumField label="Extra stops"      v={form.extra_stops} onChange={(v) => set("extra_stops", v)} />
          <NumField label="Waiting (min)"    v={form.waiting_min} onChange={(v) => set("waiting_min", v)} />
        </div>
        <button onClick={run} disabled={loading}
                className="mt-4 w-full rounded-lg bg-white text-black py-3 text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="pv2-prev-run">
          {loading && <Loader2 size={14} className="animate-spin" />} Calculate
        </button>
      </div>

      <div className="rounded-2xl border border-ink-border p-5" data-testid="pv2-prev-output">
        <div className="text-sm font-black uppercase tracking-widest text-white/60 mb-4 flex items-center gap-2">
          <Info size={14} /> Result
        </div>
        {!result && <div className="text-white/50 text-sm">Fill in the inputs and click Calculate.</div>}
        {result && !result.matched && (
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-300 p-3 text-sm" data-testid="pv2-prev-nomatch">
            <div className="font-bold mb-1">No matching rule</div>
            <div className="text-orange-200/80 text-xs">Resolved zone: {result?.breakdown?.resolved_zone_id || "—"} · Level: {result.matched_level}</div>
          </div>
        )}
        {result && result.matched && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <MetricBox label="Passenger" value={`${result.passenger_price} ${result.currency}`} testid="pv2-prev-passenger" />
              <MetricBox label="Driver payout" value={`${result.driver_payout} ${result.currency}`} accent="green" testid="pv2-prev-driver" />
              <MetricBox label="Drivons commission" value={`${result.drivon_commission} ${result.currency}`} sub={`${result.commission_percent}%`} testid="pv2-prev-commission" />
            </div>
            <div className="rounded-lg bg-ink-surface2/50 border border-ink-border p-3 text-xs">
              <div className="flex items-center justify-between text-white/60 mb-2">
                <span>Matched rule</span>
                <span className="font-mono">{result.matched_rule_id?.slice(0, 16)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                  result.matched_level === "zone" ? "bg-blue-500/20 text-blue-300" :
                  result.matched_level === "country" ? "bg-purple-500/20 text-purple-300" :
                  "bg-white/10 text-white/70"
                }`}>{result.matched_level}</span>
                <span className="text-white/50">→ {result.snapshot?.country} · {result.snapshot?.category} · {result.snapshot?.option_code || "any"}</span>
              </div>
            </div>
            <details className="rounded-lg bg-ink-surface2/50 border border-ink-border p-3 text-xs">
              <summary className="cursor-pointer text-white/70 font-bold">Breakdown</summary>
              <pre className="mt-2 text-white/60 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(result.breakdown, null, 2)}</pre>
            </details>
            <details className="rounded-lg bg-ink-surface2/50 border border-ink-border p-3 text-xs">
              <summary className="cursor-pointer text-white/70 font-bold">Rule snapshot (would be saved on order)</summary>
              <pre className="mt-2 text-white/60 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(result.snapshot, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricBox({ label, value, sub, accent, testid }) {
  return (
    <div className={`rounded-xl p-4 border ${
      accent === "green" ? "bg-green-500/10 border-green-500/30" : "bg-white/5 border-ink-border"
    }`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-white/50">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// DRIFT REPORT — bulk snapshot-vs-today across recent V2 orders
// ────────────────────────────────────────────────────────────────────────

function DriftReportTab() {
  const [limit, setLimit] = useState(100);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [minPct, setMinPct] = useState(0);

  const run = async () => {
    setLoading(true); setErr(null); setData(null);
    try {
      const { data: d } = await api.get(`/admin/pricing-v2/orders-diff-report`, { params: { limit } });
      setData(d);
    } catch (e) { setErr(formatApiError(e)); toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const rowsFiltered = (data?.rows || []).filter((r) =>
    minPct === 0 ? true : (r.delta_pct !== null && Math.abs(r.delta_pct) >= minPct)
  );

  /** Serialize the visible rows to a CSV blob and trigger a browser
   * download. Reads-only: uses the rows already in memory — no extra
   * backend call, no order mutation. `zone` resolved from the snapshot
   * `zone_id`; when null we emit the empty string so spreadsheets align. */
  const downloadCsv = () => {
    if (!rowsFiltered.length) { toast.error("Nothing to export"); return; }
    const header = [
      "order_id", "booking_date", "country", "city_zone_id", "category",
      "option", "currency", "distance_km", "duration_min",
      "snapshot_price", "current_price", "difference", "difference_percent",
      "snapshot_rule_id", "snapshot_rule_version",
      "current_rule_id", "current_rule_version", "matched_today",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      // Standard RFC 4180 CSV escaping — quote when the cell contains
      // comma / quote / newline; double any embedded quotes.
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of rowsFiltered) {
      lines.push([
        r.order_id, r.created_at, r.country, r.zone_id || "",
        r.category, r.option_code || "any", r.currency,
        r.distance_km ?? "", r.duration_min ?? "",
        r.past_price, r.current_price, r.delta_abs, r.delta_pct,
        r.past_rule_id, r.past_rule_version,
        r.current_rule_id || "", r.current_rule_version || "",
        r.matched_today ? "yes" : "no",
      ].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `drivons-drift-report-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rowsFiltered.length} rows`);
  };

  return (
    <div data-testid="pv2-drift">
      <div className="rounded-2xl border border-ink-border p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mb-1">Analyze last N orders</div>
          <input type="number" min="1" max="500" value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                 className="w-24 bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm"
                 data-testid="pv2-drift-limit" />
        </div>
        <div>
          <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mb-1">Min |Δ%| filter</div>
          <input type="number" min="0" step="0.5" value={minPct} onChange={(e) => setMinPct(Number(e.target.value))}
                 className="w-24 bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm"
                 data-testid="pv2-drift-minpct" />
        </div>
        <button onClick={run} disabled={loading}
                className="ms-auto rounded-lg px-4 py-2 text-sm font-bold bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
                data-testid="pv2-drift-run">
          {loading && <Loader2 size={14} className="animate-spin" />}
          <TrendingUp size={14} /> Run drift report
        </button>
        {data && data.rows && data.rows.length > 0 && (
          <button onClick={downloadCsv}
                  className="rounded-lg px-4 py-2 text-sm font-bold bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-2"
                  data-testid="pv2-drift-csv">
            <Download size={14} /> Download CSV
          </button>
        )}
      </div>

      {err && <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 p-3 text-sm mb-4">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <MetricBox label="Orders analyzed" value={data.count} />
            <MetricBox label="Orders with drift" value={data.drift_count}
                       sub={`${data.count ? ((data.drift_count / data.count) * 100).toFixed(0) : 0}% of total`} />
            <MetricBox label="Past total" value={data.totals.past_total.toLocaleString()} sub="mixed currencies" />
            <MetricBox label="Current total" value={data.totals.current_total.toLocaleString()} sub="mixed currencies" />
            <MetricBox label="Cumulative Δ" value={`${data.totals.cumulative_delta > 0 ? "+" : ""}${data.totals.cumulative_delta}`}
                       accent={data.totals.cumulative_delta > 0 ? "" : "green"}
                       sub={`avg ${data.totals.average_delta > 0 ? "+" : ""}${data.totals.average_delta} · max ${data.totals.largest_delta}`} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-ink-border">
            <table className="w-full text-xs">
              <thead className="bg-ink-surface2/60 text-white/60 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="px-2 py-2 text-start">Order</th>
                  <th className="px-2 py-2 text-start">Date</th>
                  <th className="px-2 py-2 text-start">Country</th>
                  <th className="px-2 py-2 text-start">Cat / Opt</th>
                  <th className="px-2 py-2 text-end">Past</th>
                  <th className="px-2 py-2 text-end">Current</th>
                  <th className="px-2 py-2 text-end">Δ abs</th>
                  <th className="px-2 py-2 text-end">Δ %</th>
                  <th className="px-2 py-2 text-center">Rule v</th>
                </tr>
              </thead>
              <tbody>
                {rowsFiltered.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-white/50">
                    {data.count === 0 ? "No V2 orders in the database yet." : "No rows match the filter."}
                  </td></tr>
                )}
                {rowsFiltered.map((r) => {
                  const drift = r.delta_abs !== null && Math.abs(r.delta_abs) > 0.001;
                  const positive = (r.delta_abs || 0) > 0;
                  return (
                    <tr key={r.order_id} className="border-t border-ink-border hover:bg-white/[0.02]">
                      <td className="px-2 py-2 font-mono text-[10px] text-white/70">{r.order_id.slice(0, 16)}</td>
                      <td className="px-2 py-2 text-white/60">{r.created_at?.slice(0, 10)}</td>
                      <td className="px-2 py-2 font-bold">{r.country}</td>
                      <td className="px-2 py-2 text-white/70">{r.category}/{r.option_code || "any"}</td>
                      <td className="px-2 py-2 text-end tabular-nums">{r.past_price} <span className="text-white/40">{r.currency}</span></td>
                      <td className="px-2 py-2 text-end tabular-nums">
                        {r.current_price !== null ? <>{r.current_price} <span className="text-white/40">{r.currency}</span></> : <span className="text-orange-300">no match</span>}
                      </td>
                      <td className={`px-2 py-2 text-end tabular-nums font-bold ${!drift ? "text-white/40" : positive ? "text-red-300" : "text-green-300"}`}>
                        {r.delta_abs !== null ? `${positive ? "+" : ""}${r.delta_abs}` : "—"}
                      </td>
                      <td className={`px-2 py-2 text-end tabular-nums ${!drift ? "text-white/40" : positive ? "text-red-300" : "text-green-300"}`}>
                        {r.delta_pct !== null ? `${positive ? "+" : ""}${r.delta_pct}%` : "—"}
                      </td>
                      <td className="px-2 py-2 text-center text-white/60">
                        {r.past_rule_version}
                        {r.current_rule_version !== r.past_rule_version && r.current_rule_version !== undefined && (
                          <span className="ms-1 text-orange-300">→{r.current_rule_version}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-white/40 mt-2">
            Historical snapshots are immutable and were only READ by this report. No order or payment amount was modified.
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// SNAPSHOT EXPLORER (per-order pricing_v2)
// ────────────────────────────────────────────────────────────────────────

function SnapshotExplorerTab() {
  const [orderId, setOrderId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (!orderId.trim()) { toast.error("Enter an order ID"); return; }
    setLoading(true); setData(null); setErr(null);
    try {
      const { data: d } = await api.get(`/admin/pricing-v2/orders/${orderId.trim()}/snapshot`);
      setData(d);
    } catch (e) {
      setErr(formatApiError(e));
      toast.error(formatApiError(e));
    }
    finally { setLoading(false); }
  };

  const pv2 = data?.pricing_v2;
  const rule = pv2?.snapshot || {};

  return (
    <div data-testid="pv2-snapshot" className="space-y-5">
      <div className="rounded-2xl border border-ink-border p-4 md:p-5">
        <div className="text-sm font-black uppercase tracking-widest text-white/60 mb-3 flex items-center gap-2">
          <FileSearch size={14} /> Explore pricing snapshot on any order
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="ord_xxxxxxxxxxxx"
            className="flex-1 min-w-[220px] bg-ink-surface2 border border-ink-border rounded-lg px-3 py-2 text-sm font-mono"
            data-testid="pv2-snap-order-input"
          />
          <button
            onClick={run}
            disabled={loading}
            className="rounded-lg px-4 py-2 text-sm font-bold bg-white text-black hover:bg-white/90 inline-flex items-center gap-2"
            data-testid="pv2-snap-run"
          >
            {loading && <Loader2 size={14} className="animate-spin" />} Load snapshot
          </button>
        </div>
        <div className="text-[11px] text-white/50 mt-2">
          The snapshot is only present on orders created while the V2 feature flag was ON. Older orders return "no snapshot".
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 p-3 text-sm" data-testid="pv2-snap-err">{err}</div>
      )}

      {data && !data.present && (
        <div className="rounded-2xl border border-orange-500/30 bg-orange-500/10 text-orange-200 p-5" data-testid="pv2-snap-none">
          <div className="font-bold mb-1">No V2 snapshot on this order</div>
          <div className="text-xs text-orange-200/80">
            The order was likely created before V2 was enabled — legacy pricing was used.
            Order total (legacy): <span className="font-mono tabular-nums">{data.order_total_legacy} {data.order_currency}</span> ·
            Country: <span className="font-bold">{data.order_country}</span> ·
            Category: <span className="font-bold">{data.order_category}</span>
          </div>
        </div>
      )}

      {data && data.present && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <MetricBox label="Passenger" value={`${pv2.passenger_price} ${pv2.currency}`} testid="pv2-snap-passenger" />
            <MetricBox label="Driver payout" value={`${pv2.driver_payout} ${pv2.currency}`} accent="green" testid="pv2-snap-driver" />
            <MetricBox label="Drivons commission" value={`${pv2.drivon_commission} ${pv2.currency}`} sub={`${pv2.commission_percent}%`} testid="pv2-snap-commission" />
          </div>

          <div className="rounded-2xl border border-ink-border p-4">
            <div className="text-[10px] uppercase tracking-widest font-black text-white/50 mb-3">Matched rule</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
              <RowKV k="Rule ID"       v={pv2.rule_id} />
              <RowKV k="Rule version"  v={pv2.rule_version} />
              <RowKV k="Matched level" v={pv2.matched_level} />
              <RowKV k="Formula"       v={rule.formula} />
              <RowKV k="Country"       v={rule.country} />
              <RowKV k="Zone"          v={rule.zone_id || "—"} />
              <RowKV k="Category"      v={rule.category} />
              <RowKV k="Option"        v={rule.option_code || "any"} />
              <RowKV k="Currency"      v={pv2.currency} />
              <RowKV k="Captured at"   v={pv2.captured_at?.slice(0, 19)?.replace("T", " ")} />
            </div>
          </div>

          <details open className="rounded-2xl border border-ink-border p-4 text-xs">
            <summary className="cursor-pointer text-white/80 font-bold text-sm">Breakdown</summary>
            <pre className="mt-3 text-white/70 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(pv2.breakdown, null, 2)}</pre>
          </details>

          <details className="rounded-2xl border border-ink-border p-4 text-xs">
            <summary className="cursor-pointer text-white/80 font-bold text-sm">Full rule snapshot (locked at booking)</summary>
            <pre className="mt-3 text-white/70 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(pv2.snapshot, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}
